import { useEffect, useRef } from "react";

const DEFAULT_SCROLL_THRESHOLD = 100;

function resolveScrollContainer(
  element: HTMLElement,
  findParentScrollContainer: boolean,
): HTMLElement | null {
  if (findParentScrollContainer) {
    const parent = element.closest(".overflow-y-auto");
    return parent instanceof HTMLElement ? parent : null;
  }
  return element;
}

export type UseInfiniteScrollOptions = {
  /**
   * When set (e.g. `chats.length`), re-checks whether the list still fits the viewport
   * without scrolling — if so, loads more so pagination works in short dropdowns.
   */
  contentLength?: number;
  findParentScrollContainer?: boolean;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
  scrollThreshold?: number;
};

/**
 * Hook for implementing infinite scroll functionality.
 * Triggers loadMore when user scrolls near the bottom of the container.
 *
 * @param options - Configuration options for infinite scroll
 * @param options.findParentScrollContainer - If true, finds the nearest parent with overflow-y-auto (default: false)
 * @param options.hasMore - Whether there are more items to load
 * @param options.isLoading - Whether initial data is loading
 * @param options.isLoadingMore - Whether more data is currently being loaded
 * @param options.loadMore - Callback function to load more items
 * @param options.scrollThreshold - Distance from bottom (in px) to trigger load (default: 100)
 * @param options.contentLength - Bumps a fill-viewport pass when list size changes (e.g. after fetch)
 * @returns Object containing scrollContainerRef to attach to the scrollable container
 */
export default function useInfiniteScroll({
  contentLength,
  findParentScrollContainer = false,
  hasMore,
  isLoading,
  isLoadingMore,
  loadMore,
  scrollThreshold = DEFAULT_SCROLL_THRESHOLD,
}: UseInfiniteScrollOptions) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = scrollContainerRef.current;
    if (!element) return;

    const scrollContainer = resolveScrollContainer(
      element,
      findParentScrollContainer,
    );
    if (!scrollContainer) return;

    const handleScroll = () => {
      if (!scrollContainer) return;
      const { clientHeight, scrollHeight, scrollTop } = scrollContainer;

      if (
        scrollHeight - scrollTop - clientHeight < scrollThreshold &&
        hasMore &&
        !isLoadingMore &&
        !isLoading
      ) {
        loadMore();
      }
    };

    scrollContainer.addEventListener("scroll", handleScroll, {
      passive: true,
    });
    return () => scrollContainer?.removeEventListener("scroll", handleScroll);
  }, [
    findParentScrollContainer,
    hasMore,
    isLoadingMore,
    isLoading,
    loadMore,
    scrollThreshold,
  ]);

  useEffect(() => {
    const checkFillViewport = () => {
      const element = scrollContainerRef.current;
      if (!element) return;
      const scrollContainer = resolveScrollContainer(
        element,
        findParentScrollContainer,
      );
      if (!scrollContainer) return;
      const { clientHeight, scrollHeight, scrollTop } = scrollContainer;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      if (
        distanceFromBottom < scrollThreshold &&
        hasMore &&
        !isLoadingMore &&
        !isLoading
      ) {
        loadMore();
      }
    };

    const id = requestAnimationFrame(() => {
      checkFillViewport();
    });
    return () => cancelAnimationFrame(id);
  }, [
    contentLength,
    findParentScrollContainer,
    hasMore,
    isLoadingMore,
    isLoading,
    loadMore,
    scrollThreshold,
  ]);

  return { scrollContainerRef };
}
